import { useState, useRef, useEffect, useMemo } from "react";
import "../../../css/CheckDetails/PEPSanctions/PEPSanctions.css";
import "../../../css/CheckDetails/PEPSanctions/PepSanDropdown.css";
import YesButton from "../Buttons/YesButton";
import { MY_CLIENT_REASON } from "../../CheckDetails/Utils/getConstants";

const PepSanMatchReasonDropdown = ({
  reasons = [],
  onClose,
  value = [],
  onConfirm,
  isRCA = false,
  existingRcaDetails = "",
}) => {
  const [selected, setSelected] = useState(() => {
    const init = {};
    (value || []).forEach((r) => {
      init[r] = true;
    });
    return init;
  });

  const [rcaDetails, setRcaDetails] = useState(existingRcaDetails || "");
  const [error, setError] = useState(false);
  const [rcaDetailsError, setRcaDetailsError] = useState(false);

  const menuRef = useRef(null);

  const norm = (s) => (s ?? "").toString().trim().toLowerCase();

  const MY_CLIENT_SET = useMemo(
    () => new Set((MY_CLIENT_REASON || []).map(norm)),
    [],
  );

  const isMyClient = (r) => MY_CLIENT_SET.has(norm(r));

  const selectedList = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected],
  );

  const isRcaNotMatchSelected = selectedList.some(
    (reason) => norm(reason) === "the rca is not a match",
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const toggleReason = (reason) => {
    setSelected((prev) => {
      const next = { ...prev };

      const wasChecked = !!next[reason];
      const willBeChecked = !wasChecked;

      if (isMyClient(reason)) {
        Object.keys(next).forEach((k) => {
          next[k] = false;
        });

        next[reason] = willBeChecked;
      } else {
        next[reason] = willBeChecked;

        if (willBeChecked) {
          Object.keys(next).forEach((k) => {
            if (isMyClient(k)) next[k] = false;
          });
        }
      }

      if (Object.values(next).some(Boolean)) {
        setError(false);
      }

      return next;
    });
  };

  useEffect(() => {
    const init = {};
    (value || []).forEach((r) => (init[r] = true));

    const hasMyClient = Object.keys(init).some((k) => init[k] && isMyClient(k));

    if (hasMyClient) {
      Object.keys(init).forEach((k) => {
        init[k] = isMyClient(k);
      });
    }

    setSelected(init);
    setError(false);
  }, [value]);

  useEffect(() => {
    setRcaDetails(existingRcaDetails || "");
  }, [existingRcaDetails]);

  useEffect(() => {
    if (!isRcaNotMatchSelected) {
      setRcaDetailsError(false);
    }
  }, [isRcaNotMatchSelected]);

  const handleConfirm = () => {
    if (selectedList.length === 0) {
      setError(true);
      return;
    }

    if (isRCA && isRcaNotMatchSelected && !rcaDetails.trim()) {
      setRcaDetailsError(true);
      return;
    }

    onConfirm?.(selectedList, {
      rcaDetails: isRcaNotMatchSelected ? rcaDetails.trim() : "",
    });

    onClose?.();
  };

  return (
    <div
      className={`pep-san-reason-menu ${
        isRCA && isRcaNotMatchSelected
          ? "pep-san-reason-menu--rca-not-match"
          : ""
      }`}
      ref={menuRef}
    >
      {reasons.map((reason, idx) => (
        <label key={idx} className="pep-san-reason-item">
          <input
            type="checkbox"
            className={`pep-san-checkbox ${
              error ? "pep-san-checkbox-error" : ""
            }`}
            checked={!!selected[reason]}
            onChange={() => toggleReason(reason)}
          />

          <div className="AML-Text-Body">{reason}</div>
        </label>
      ))}

      {error && (
        <div className="AML-Text-Body" style={{ color: "var(--Error)" }}>
          Please select a reason.
        </div>
      )}

      {isRCA && isRcaNotMatchSelected && (
        <div className="pep-san-rca-details-box">
          <p className="AML-Text-Body">
            The alert may relate to a PEP or sanctioned individual, so please
            provide sufficient detail to explain why you are satisfied this is a
            false positive, taking into account the information shown in the
            alert.
          </p>

          <textarea
            className={`pep-san-rca-details-textarea AML-Text-Body ${
              rcaDetailsError ? "pep-san-rca-details-textarea-error" : ""
            }`}
            placeholder="Enter details *"
            value={rcaDetails}
            onChange={(e) => {
              setRcaDetails(e.target.value);
              setRcaDetailsError(false);
            }}
            rows={4}
          />

          {rcaDetailsError && (
            <div className="AML-Text-Body" style={{ color: "var(--Error)" }}>
              Please enter additional details.
            </div>
          )}
        </div>
      )}

      <div className="pep-san-reason-confirm">
        <YesButton buttonText="Save" onClick={handleConfirm} />
      </div>
    </div>
  );
};

export default PepSanMatchReasonDropdown;
